/**
 * The app-wide statement identity string. A statement reads the SAME everywhere
 * it appears — the mission-control worklist, the escalation pills, and the step
 * screens — so a reviewer who did not build the pipeline can name what a row is.
 *
 * Format: `{type} · {verdict}` where `type` is the framework's own slug
 * (`reach-svhc`, `rohs`, …) and `verdict` is a title-specific short label. A
 * statement with no verdict renders as the slug alone (that gap is itself a
 * step-1 needs-input state — see `classifyStatement`). On a list-version
 * collision (two statements share a slug + verdict, differing only by edition) a
 * numeric suffix disambiguates them — see `collisionNumbers`.
 *
 * PURE + client-safe. Ported verbatim from the studio `src/core/statementTitle.ts`.
 * The verdict SLUG is a stable identifier, not user copy; the verdict word is
 * localized through `verdictMeta` (declarations/review/verdict.ts) — the same
 * table the verdict chip reads — so the title reads the same verdict wording
 * everywhere it appears.
 */
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { verdictMeta } from "../verdict"
import type { Verdict } from "./studioTypes"

/** Minimal translate signature the title helpers need. */
export type TitleTranslate = (key: TranslationKeys) => string

/**
 * The verdict as it reads inside a statement title, or null when unset.
 * Localized via `verdictMeta` when a `translate` is passed; without one
 * (internal collision-key grouping, which never renders) falls back to the
 * raw code with underscores turned into spaces.
 */
export function verdictTitleLabel(
  verdict: Verdict | null | undefined,
  translate?: TitleTranslate,
): string | null {
  if (!verdict) return null
  if (translate) return translate(verdictMeta(verdict).labelKey)
  return verdict.replace(/_/g, " ")
}

/**
 * The statement identity string for `(slug, verdict, collisionNumber)`. Pass a
 * null/absent collision number for the (common) unique case — no suffix. A
 * colliding member's number starts at 2 (the most current stays unnumbered), so
 * a `1` is never shown.
 *
 * `translate`, when given, localizes the verdict word (see `verdictTitleLabel`).
 * Omit it only for the internal collision-key grouping in `collisionNumbers`,
 * which never reaches the screen — every render site must pass `translate`.
 */
export function statementTitle(
  slug: string,
  verdict: Verdict | null | undefined,
  collision?: number | null,
  translate?: TitleTranslate,
): string {
  const v = verdictTitleLabel(verdict, translate)
  const base = v ? `${slug} · ${v}` : slug
  return collision && collision >= 2 ? `${base} · ${collision}` : base
}

/** One statement's inputs to the run-wide collision numbering. */
export interface CollisionItem {
  id: string
  slug: string
  verdict: Verdict | null
  listVersion: string | null
  groupIndex: number
  /** ISO date the statement's list version became effective, or null when the
   *  list version is unset or not resolvable in the CMS catalog. */
  published: string | null
}

/**
 * Collision numbers keyed by statement id, computed RUN-WIDE (over the whole
 * statement set) so a given statement shows the same number on every screen.
 * Statements that render the same base title (`statementTitle(slug, verdict,
 * null)`) collide; within a colliding group the most current (latest
 * list-version `published`) stays unnumbered (null) and each older one gets
 * 2, 3, 4 … A statement whose title is unique gets null.
 *
 * A null `published` (unset or unresolvable list version) sorts LAST (least
 * current), tie-broken by `groupIndex`. Real collisions have distinct list
 * versions → distinct dates → no ties.
 *
 * The grouping key is deliberately computed WITHOUT `translate` (raw verdict
 * codes, not localized words) — see `statementTitle`'s own doc — so numbering
 * stays stable across a language switch. This is safe from a translation
 * COLLAPSING two distinct codes onto one rendered label (e.g. an unmapped
 * code and `unknown` both reading "Unknown"): `verdict` here is typed
 * `Verdict`, the five-code CMS enum (studioTypes.ts's `VERDICTS`), not the
 * open-ended MASTER-DATA status vocabulary `knownVerdictMeta` reads — so
 * every code this function ever sees already has its own exact `VERDICT_META`
 * entry and its own distinct raw-string key.
 */
export function collisionNumbers(
  items: CollisionItem[],
): Map<string, number | null> {
  const groups = new Map<string, CollisionItem[]>()
  for (const it of items) {
    const key = statementTitle(it.slug, it.verdict, null)
    ;(groups.get(key) ?? groups.set(key, []).get(key)!).push(it)
  }
  const out = new Map<string, number | null>()
  for (const members of groups.values()) {
    if (members.length === 1) {
      out.set(members[0].id, null)
      continue
    }
    const sorted = [...members].sort((a, b) => {
      // Most current first: a later published date wins; a null date sorts last.
      if (a.published && b.published) {
        if (a.published !== b.published)
          return a.published < b.published ? 1 : -1
      } else if (!!a.published !== !!b.published) {
        return a.published ? -1 : 1
      }
      return a.groupIndex - b.groupIndex
    })
    sorted.forEach((it, i) => out.set(it.id, i === 0 ? null : i + 1))
  }
  return out
}
